import {
	Action,
	ClampVariableRule,
	Condition,
	ConditionRule,
	LoadMyWheelsBasePriceRule,
	RuleDimension,
	RuleSet,
	RuleSetRule,
	Rule,
} from "@/modules/rule_engine/ruleEngineClasses";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";

type RuleTypeMap = {
	condition: ConditionRule;
	rule_set: RuleSetRule;
	load_my_wheels_base_price: LoadMyWheelsBasePriceRule;
	clamp_variable: ClampVariableRule;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * `data` is an untrusted backend payload, so it is intentionally `any` at this
 * boundary. Everything past it is typed.
 */

const parseConditions = (data: any): Condition[] =>
	(data.conditions ?? []).map((condition: any) => parseCondition(condition));

export function parseRule<T extends keyof RuleTypeMap>(
	data: any
): RuleTypeMap[T] {
	if (data.type === "condition") {
		const actions: Action[] = (data.actions ?? []).map((action: any) => ({
			uuid: action.uuid,
			type: action.type,
			variable: action.variable,
			amount: action.amount,
			label_id: action.label_id,
			task_id: action.task_id,
		}));

		return {
			uuid: data.uuid,
			type: "condition",
			conditions: parseConditions(data),
			actions,
		} as RuleTypeMap[T];
	}

	if (data.type === "load_my_wheels_base_price") {
		return {
			uuid: data.uuid,
			type: "load_my_wheels_base_price",
			conditions: parseConditions(data),
		} as RuleTypeMap[T];
	}

	if (data.type === "clamp_variable") {
		return {
			uuid: data.uuid,
			type: "clamp_variable",
			variable_name: data.variable_name,
			min_value: data.min_value,
			max_value: data.max_value,
			conditions: parseConditions(data),
		} as RuleTypeMap[T];
	}

	if (data.type === "rule_set") {
		const ruleSet: RuleSet = {
			uuid: data.rule_set.uuid,
			name: data.rule_set.name,
			rules: (data.rule_set.rules ?? []).map(
				(rule: any): Rule => parseRule(rule)
			),
			version_type: data.rule_set.version_type,
		};

		return {
			uuid: data.uuid,
			type: "rule_set",
			rule_set: ruleSet,
			conditions: parseConditions(data),
		} as RuleTypeMap[T];
	}

	throw new Error(`Unknown rule type: ${data.type}`);
}

/**
 * Resolves the dimension a condition refers to.
 *
 * The resolved dimension is copied before `ab_test_uuid` is applied. The store
 * hands back the shared `ruleDimensions` entry, so writing to it directly leaked
 * one condition's A/B test onto every other condition using that dimension.
 */
const resolveDimension = (data: any): RuleDimension => {
	if (!data.dimension_uuid) {
		return data.dimension ?? null;
	}

	const dimension = useRuleEnginedStore().findRuleDimensionByUuid(
		data.dimension_uuid
	);

	if (!dimension) {
		throw new Error(`Unknown rule dimension: ${data.dimension_uuid}`);
	}

	return { ...dimension, ab_test_uuid: data.ab_test_uuid ?? null };
};

export function parseCondition(data: any): Condition {
	const dimension = resolveDimension(data);
	const uuid = data.uuid;

	// An unconfigured row round-trips as a draft rather than throwing, so a rule
	// holding one can still be cloned or re-parsed while the editor is open.
	if (data.type === "draft" || data.type === null || data.type === undefined) {
		return { uuid, dimension, type: "draft" };
	}

	switch (data.type) {
		case "in_number":
			return { uuid, dimension, type: "in_number", values: data.values };
		case "in_string":
			return { uuid, dimension, type: "in_string", values: data.values };
		case "not_in_number":
			return { uuid, dimension, type: "not_in_number", values: data.values };
		case "not_in_string":
			return { uuid, dimension, type: "not_in_string", values: data.values };
		case "day_of_week":
			return { uuid, dimension, type: "day_of_week", values: data.values };
		case "ab_test":
			return { uuid, dimension, type: "ab_test", values: data.values };
		case "time_of_day":
			return { uuid, dimension, type: "time_of_day", value: data.value };
		case "comparison_string":
			return {
				uuid,
				dimension,
				type: "comparison_string",
				operator: data.operator,
				value: data.value,
			};
		case "comparison_number":
			return {
				uuid,
				dimension,
				type: "comparison_number",
				operator: data.operator,
				value: data.value,
			};
		case "comparison_date":
			return {
				uuid,
				dimension,
				type: "comparison_date",
				operator: data.operator,
				value: data.value,
			};
		case "comparison_boolean":
			return {
				uuid,
				dimension,
				type: "comparison_boolean",
				operator: data.operator,
				value: data.value,
			};
		case "between_date":
			return {
				uuid,
				dimension,
				type: "between_date",
				from: data.from,
				to: data.to,
			};
		case "between_number":
			return {
				uuid,
				dimension,
				type: "between_number",
				from: data.from,
				to: data.to,
			};
		case "in_hierarchy":
			return {
				uuid,
				dimension,
				type: "in_hierarchy",
				hierarchy_definition_id: data.hierarchy_definition_id,
				scope: data.scope ?? null,
				selection_depth: data.selection_depth ?? 1,
				excluded_branches: data.excluded_branches ?? [],
			};
		case "not_in_hierarchy":
			return {
				uuid,
				dimension,
				type: "not_in_hierarchy",
				hierarchy_definition_id: data.hierarchy_definition_id,
				scope: data.scope ?? null,
				selection_depth: data.selection_depth ?? 1,
				excluded_branches: data.excluded_branches ?? [],
			};
		default:
			throw new Error(`Unknown condition type: ${data.type}`);
	}
}

export function injectDimensionIds<T>(input: T): T {
	const deepCloneAndInject = (obj: any): any => {
		if (Array.isArray(obj)) {
			return obj.map(deepCloneAndInject);
		}

		if (obj && typeof obj === "object") {
			const newObj: any = {};

			for (const key in obj) {
				if (Object.prototype.hasOwnProperty.call(obj, key)) {
					newObj[key] = deepCloneAndInject(obj[key]);
				}
			}

			// If object has dimension, add dimension_id
			if (
				"dimension" in obj &&
				obj.dimension &&
				typeof obj.dimension.uuid !== "undefined"
			) {
				newObj["dimension_uuid"] = obj.dimension.uuid;
			}

			return newObj;
		}

		return obj;
	};

	return deepCloneAndInject(input);
}
